// plan-03 S04: boot sequence — bible §6
// plan-12 S06: extended to 12 lines + tagline with first-visit animation

import { streamText, createStreamTarget, enableSkip, disableSkip } from './streaming'
import { play } from '../audio/sfx'
import { load, save } from '../core/save'
import { shouldRunPost, runPost } from '../postgrade/passes/biosPost'
import type { CursorController } from './cursor'

const ART_LINES = new Set([2, 3, 4, 5, 6, 7, 8])
// plan-12 S06: tagline line (after the box art) gets first-visit animation
const TAGLINE_LINE = 10

const BOOT_CPS = 160 // faster than LOG_CPS (36) — ~4s for full MOTD on first visit
const LOGO_DELAY_MS = 80 // per-line reveal for first-boot tagline

export async function runBoot(
  history: HTMLElement,
  cursor: CursorController,
  signal?: AbortSignal,
): Promise<void> {
  enableSkip()

  // plan-17 S01: BIOS POST runs before boot on first cold load only
  if (shouldRunPost()) {
    await runPost(history)
  }

  const s = load()
  const reduced = document.body.classList.contains('vt-reduced-motion')
  const firstBoot = !s.polishSeenLogo && s.visits <= 0
  const returningVisitor = s.visits > 1

  const visits = s.visits + 1
  const firstSeen = new Date(s.firstSeenAt)
  const firstH = String(firstSeen.getUTCHours()).padStart(2, '0')
  const firstM = String(firstSeen.getUTCMinutes()).padStart(2, '0')
  const firstS = String(firstSeen.getUTCSeconds()).padStart(2, '0')
  const firstD = firstSeen.toISOString().slice(0, 10)
  const uptimeDays = Math.floor((Date.now() - s.firstSeenAt) / 86400000)

  // boot stream — 12 lines (extended from 9 per plan-12 S06)
  const lines: string[] = [
    // [0] kernel banner
    'pattern authority oracle kernel 48.0.48-PROBABLY #48 SMP preempt_dynamic consensus x86_64',
    '',
    // [2-9] ascii art — every line hand-verified
    '        ___',         //  8 + 3 = 11
    '       /48 \\',       //  7 + 5 = 12
    '       \\___/',       //  7 + 5 = 12
    '   +----------+',     //  3 + 12 = 15
    '   | VAGUEPOST|',    //  3 + 12 = 15
    '   |   KING   |',    //  3 + 12 = 15
    '   +----------+',    //  3 + 12 = 15
    // [10] tagline (plan-12 S06: first-visit animation gate)
    '   vaguepost king · oracle terminal · v 2.0',
    // [11] welcome
    'welcome to the oracle. consensus: probably.',
    '',
    // [13-17] motd
    'this terminal hosts a set of useless tools that can be used for vague',
    'divination. type help for a list of commands. start with:',
    '  directory     -- observed consensus nodes',
    '  consensus W   -- run heatmap for word W',
    '  divine        -- generate a vaguepost',
    '',
    // plan-12 S06: extended boot stream — 3 new lines
    'node 04: handshake.',
    'node 48: synchronized.',
    'quorum: 48 of 48. probably.',
    '',
    // [22-23] system info
    `system: quorum 48/48 | uptime ${uptimeDays}d | session visits: ${visits} | oracle: warm`,
    `last seen: ${firstD} ${firstH}:${firstM}:${firstS} UTC`,
    '',
  ]

  for (let i = 0; i < lines.length; i++) {
    if (signal?.aborted) break

    const line = lines[i]
    const target = createStreamTarget(history, 'vt-line-boot')
    target.style.color = 'var(--vt-green-dim)'

    if (ART_LINES.has(i)) {
      target.style.color = 'var(--vt-king-green)'
    } else if (i === TAGLINE_LINE) {
      target.style.color = 'var(--vt-king-green)'
      target.style.fontStyle = 'italic'

      // first-boot: brief pause before the tagline appears
      if (firstBoot && !reduced) {
        await new Promise<void>((resolve) => {
          setTimeout(resolve, LOGO_DELAY_MS * 4) // ~320ms pause
        })
        s.polishSeenLogo = true
        save(s)
      }
    } else if (line.startsWith('welcome')) {
      target.style.color = 'var(--vt-term-green)'
    }

    cursor.attach(target)

    if (returningVisitor) {
      target.textContent = line
    } else {
      await streamText(target, line, {
        cps: BOOT_CPS,
        sfx: false,
        signal,
      })
    }
  }

  play('boot_chime')

  cursor.detach()
  disableSkip()
}
